import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";


function Economics38() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-37", 
        index: "/index-economics",                 
        next: "/economics-39", 
      };
        const titleText ="The Case Against Rent Control";

        const questions = [
  {
    text: "1. What is one of the primary reasons given by governments to implement rent control measures?",
    options: [
      { value: "A", label: "A. To increase government revenue from property taxes" },
      { value: "B", label: "B. To encourage the building of more luxury apartments" },
      {
        value: "C",
        label:
          "C. To prevent landlords from exploiting tenants during housing shortages",
      },
      { value: "D", label: "D. To reduce the size of urban populations" },
    ],
    correct: "C",
  },
  {
    text: "2. What is a common unintended consequence of rent control on existing housing stock?",
    options: [
      { value: "A", label: "A. Tenants move out quickly to save money" },
      { value: "B", label: "B. Landlords upgrade the quality of housing" },
      {
        value: "C",
        label:
          "C. Occupants remain in underused or deteriorating properties",
      },
      { value: "D", label: "D. Property values increase significantly" },
    ],
    correct: "C",
  },
  {
    text: "3. How does rent control affect the incentive to build budget housing?",
    options: [
      { value: "A", label: "A. It increases investment in low-income housing" },
      {
        value: "B",
        label: "B. It discourages construction of luxury housing only",
      },
      {
        value: "C",
        label: "C. It encourages balanced growth across housing sectors",
      },
      {
        value: "D",
        label: "D. It discourages construction of budget housing",
      },
    ],
    correct: "D",
  },
  {
    text: "4. What often happens when governments subsidize certain tenant groups through rent control?",
    options: [
      { value: "A", label: "A. Urban development flourishes" },
      { value: "B", label: "B. Taxpayer burden increases" },
      { value: "C", label: "C. Property maintenance improves" },
      {
        value: "D",
        label:
          "D. Private investors are more attracted to the housing market",
      },
    ],
    correct: "B",
  },
];

  
    // State to track selected answers for all questions
        const [answers, setAnswers] = useState({});
      
        // Function to update an answer
        const handleAnswerChange = (questionIndex, selectedValue) => {
          setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
        };
      
        // Compute score
        const score = questions.reduce((total, q, index) => {
          return total + (answers[index] === q.correct ? 1 : 0);
        }, 0);

  return (
    <div className="container">
    <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={38}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 38</b>! Today we explore the often misunderstood world
        of rent control, which is a policy that may appear to offer relief to
        tenants but in reality creates deeper economic distortions. Through this
        lesson, we will examine how government-imposed rent ceilings disrupt
        housing markets, discourage new construction, and ultimately harm the
        very people they intend to help. As we peel back the layers of
        well-meaning legislation, we’ll uncover the long-term consequences of
        interfering with the natural dynamics of supply and demand in real
        estate.
        <br />
        <br /> <b>The Political Origins of Rent Control</b>
        <br />
        <br />
        Rent control, at its core, is another manifestation of
        government-imposed price-fixing, specifically targeting the housing
        sector. While general price controls may sweep across a wide range of
        consumer goods and services during periods of inflation or crisis, rent
        control tends to operate through targeted legislation. It is often
        introduced with strong emotional and political undertones. These
        policies are typically enacted during times of economic turmoil, war, or
        rapid urbanization, where the demand for housing surges beyond the
        immediate supply. For instance, during wartime, buildings may be
        destroyed, leading to a shortage of available housing units. Similarly,
        when industrialization accelerates in a particular locality, it draws
        large numbers of workers who require nearby accommodation. In such
        cases, the claim made by lawmakers is that housing supply is not elastic
        enough to meet the sudden spike in demand. Because buildings cannot be
        constructed overnight, politicians argue that landlords must be legally
        restrained from exploiting tenants through high rents.
        <br />
        These arguments are often presented under the banner of compassion,
        fairness, and protection for the vulnerable. Yet what is overlooked is
        the complex nature of how housing markets function in a free society.
        Prices are not arbitrary impositions on the public by landlords.
        Instead, they are vital signals that regulate demand and allocate
        resources efficiently. When these signals are tampered with through rent
        control, the results are rarely favorable to the very people the
        legislation claims to protect. Indeed, the interference with price
        discovery disrupts the natural behavior of both renters and builders.
        Despite good intentions, rent control policies inevitably lead to
        long-term consequences that are economically inefficient and socially
        unjust. This is because such policies misunderstand the role of prices
        in coordinating individual action within the housing market. Rather than
        allowing the market to respond to changes in supply and demand, the
        state imposes static pricing in a dynamic environment.
        <br />
        In a functioning housing market, rising rents in response to higher
        demand naturally encourage people to economize their use of space.
        Families may downsize, single individuals may choose to cohabitate, and
        unused rooms might be rented out. This organic reallocation of housing
        ensures that more people can be accommodated within the same physical
        space. When the state intervenes and freezes rents below the market
        rate, this adjustment process is halted. The result is that existing
        tenants continue to occupy larger spaces than they need while newcomers
        are left scrambling for limited housing options. The pain of high rent
        is not eliminated, only redistributed. Instead of higher prices
        encouraging expansion and efficient usage, the burden is shifted to the
        newly arrived workers, immigrants, or low-income families who find
        themselves locked out of affordable housing altogether.
        <br />
        <br />
        <b>Consequences of Suppressing Market Signals</b>
        <br />
        <br />
        When the government fixes rents at artificially low levels, the most
        immediate consequence is that the supply of housing contracts. Builders,
        recognizing that the financial return on their investments will be
        capped regardless of cost, refrain from constructing new buildings. Even
        those willing to build may delay projects due to the fear that future
        regulations will retroactively subject their properties to rent
        controls. Over time, the result is a stagnation in the construction of
        affordable housing. Ironically, when the state realizes that its earlier
        policies have led to a housing crisis, it may exempt new buildings from
        rent control in an attempt to encourage development. But this partial
        correction introduces new problems.
        <br />
        For example, the occupants of older rent-controlled buildings have no
        reason to move out even if their need for space has diminished. A couple
        whose children have moved out will continue occupying a large apartment
        because the rent is fixed far below the market value. This behavior
        leads to a gross misallocation of space. In some cities, it is not
        uncommon to find elderly individuals occupying spacious apartments while
        growing families are forced into cramped units or driven into the
        outskirts. Furthermore, landlords of rent-controlled buildings have no
        incentive to maintain or renovate their properties. With revenues capped
        and maintenance costs rising, these buildings gradually fall into
        disrepair. The decay of such buildings is not merely an aesthetic issue.
        It reflects a deeper economic inefficiency, where resources are being
        used suboptimally and future investment is discouraged.
        <br />
        As old buildings become uninhabitable, pressure mounts on the limited
        number of new constructions that are not subject to rent control. These
        properties, now in high demand, experience skyrocketing prices.
        Ironically, the very policy that was intended to keep rents low ends up
        increasing rental prices in the unregulated sector. This creates a
        housing market divided between the privileged few living in underpriced
        units and the majority forced to pay exorbitant prices or settle for
        deteriorating living conditions. The inequality generated by rent
        control is not natural but a product of economic distortion introduced
        by the state.
        <br />
        In addition, the fear of future rent controls casts a long shadow over
        potential investors and developers. The uncertainty regarding future
        regulation adds a political risk premium to housing investments.
        Builders may decide that it is safer and more profitable to invest in
        luxury condominiums, which are often exempt from rent control laws. As a
        result, the market tilts away from constructing the kind of budget
        housing that low and middle-income families need. This distortion of
        incentives leads to a structural shortage of affordable homes, making
        the housing crisis even worse than it would have been under market
        conditions.
        <br />
        <br />
        <b>The Perverse Incentives of Targeted Rent Controls</b>
        <br />
        <br />
        Another strategy employed by governments is the selective application of
        rent control. This often means targeting only budget housing or
        low-income neighborhoods while leaving luxury apartments and high-end
        buildings untouched. While this may seem like a balanced approach, it
        again leads to perverse outcomes. Developers, recognizing that only
        budget housing will be subject to regulation, simply choose to focus on
        luxury projects where the returns are higher and the risk of government
        interference is lower. The result is an overproduction of luxury
        apartments and an underproduction of affordable housing.
        <br />
        This exacerbates social inequality in a very tangible way. The supply of
        budget housing shrinks while demand for it continues to rise. Those in
        need of affordable shelter are left with increasingly few options.
        Furthermore, landlords who operate under rent control are unable to
        raise rents even when their costs of maintenance, repair, and property
        taxes increase. This leaves them with no choice but to reduce the
        quality of the services they offer. Basic amenities like plumbing,
        electricity, pest control, and cleanliness begin to deteriorate. In the
        worst cases, entire neighborhoods fall into a state of urban decay.
        <br />
        Over time, these neighborhoods may turn into slums. Properties are left
        unmaintained, public infrastructure deteriorates, and crime often
        increases. All of this is not due to the greed of landlords or
        developers but due to policies that have removed the economic incentives
        required to maintain and improve housing stock. In effect, the state has
        made it unprofitable to care for poor neighborhoods. This leads to a
        cruel paradox. The very people whom rent control was meant to protect
        are those who suffer most as their living conditions deteriorate and
        their housing options disappear.
        <br />
        The situation becomes even worse when city governments, facing declining
        property values and tax revenues, are forced to reduce public services.
        Schools, sanitation, law enforcement, and transportation all suffer. The
        tax base shrinks as property values decline and new development halts.
        The city, once a vibrant economic ecosystem, begins a slow decline into
        financial insolvency. In some cases, municipalities have been pushed
        into bankruptcy, not by lack of resources but by the burden of
        economically illiterate policies.
        <br />
        <br />
        <b>Political Exploitation and Misplaced Blame</b>
        <br />
        <br />
        The final tragedy of rent control is not just economic but political. In
        many instances, these policies are used by politicians to secure votes
        rather than to improve the housing situation. By offering subsidized
        housing to certain demographics, often based on political
        considerations, governments create a class of citizens dependent on the
        state. These citizens may become reliable voting blocs, supporting the
        very politicians who have distorted the housing market. This
        politicization of housing policy is deeply harmful. Instead of focusing
        on rational, market-based solutions that benefit all, housing becomes a
        tool for maintaining political power.
        <br />
        Moreover, when the failures of rent control become undeniable,
        governments rarely admit their mistakes. Instead, they shift the blame
        onto the private sector. Developers are accused of being greedy.
        Landlords are painted as exploitative. The concept of privatization is
        vilified as being inherently incapable of addressing housing needs. In
        truth, it is the government’s interference in the pricing mechanism and
        property rights that has caused the disaster. Yet the narrative is often
        manipulated to serve the interests of those in power. Public anger is
        redirected away from those who implemented the flawed policies and
        toward those trying to operate within the distorted rules.
        <br />
        This political deflection allows the cycle to continue. New forms of
        rent control may be proposed. More subsidies may be granted. More
        regulations may be introduced, each compounding the problems created by
        the last. Instead of learning from the failure of centralized control,
        governments double down. The ultimate losers in this cycle are the poor,
        the working class, and the honest builders and landlords who want to
        provide good housing but are shackled by irrational laws.
        <br />
        The long-term solution is not more intervention but less. Housing, like
        any other good, responds to supply and demand. If rents are high, it
        signals a shortage and incentivizes construction. If rents fall, it
        signals an oversupply. These signals are essential for ensuring that
        resources are allocated efficiently and that everyone has access to the
        housing they need. Only by restoring the integrity of market prices and
        protecting property rights can we solve the housing crisis and build
        cities that are both humane and prosperous.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Rent control is not a policy of compassion but one of confusion. By
        freezing prices in a dynamic market, it distorts incentives, reduces
        supply, deteriorates quality, and politicizes housing. While it promises
        protection, it delivers decay. The very people it aims to help suffer
        the most from its unintended consequences. Instead of curbing greed, it
        punishes productivity. Instead of alleviating shortages, it deepens
        them. Housing markets need freedom to function. They require prices that
        reflect reality, not political sentiment. Only when we respect the
        forces of supply and demand, and honor the principles of private
        property, can we ensure affordable and dignified housing for all.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In the next lesson, we turn our attention to a form of price control
        that often escapes criticism i.e minimum wage laws. While many people
        recognize the distortions caused by rent control or price ceilings on
        goods, they may still advocate for minimum wages, believing it protects
        low-income workers. But is this truly the case? We’ll explore how
        setting a legal floor on wages impacts employment, productivity, and the
        overall economy and why such well-meaning policies often backfire,
        hurting the very individuals they intend to help.
      </p>
      <h2>Test your understanding</h2>
     {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics38;
